import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { neighborhoodBySlug, neighborhoodsByCity } from "@/lib/data/neighborhoods";
import { cityBySlug } from "@/lib/data/cities";
import { serviceBySlug, services as allServices } from "@/lib/data/services";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { ArrowUpRight } from "lucide-react";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1579454577332-6c8a8f99a23c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDQ2NDJ8MHwxfHNlYXJjaHw0fHx3aW5uaXBlZyUyMHNreWxpbmUlMjB3aW50ZXJ8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function NeighborhoodDetail() {
  const { citySlug, neighborhoodSlug } = useParams();
  const neighborhood = neighborhoodBySlug(neighborhoodSlug);
  const city = cityBySlug(citySlug);

  useSEO({
    title: neighborhood && city
      ? `${neighborhood.name} Security — ${city.name} | Manitoba Security Services`
      : "Neighborhood — Manitoba Security Services",
    description: neighborhood ? neighborhood.hero : undefined,
    keywords: neighborhood ? neighborhood.keywords : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/cities/${citySlug}/${neighborhoodSlug}`
        : `/cities/${citySlug}/${neighborhoodSlug}`,
    jsonLd:
      neighborhood && city
        ? {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `Manitoba Security Services — ${neighborhood.name}, ${city.name}`,
            description: neighborhood.hero,
            areaServed: {
              "@type": "Place",
              name: `${neighborhood.name}, ${city.name}`,
              containedInPlace: {
                "@type": "City",
                name: city.name,
                containedInPlace: {
                  "@type": "AdministrativeArea",
                  name: "Manitoba",
                  containedInPlace: { "@type": "Country", name: "Canada" },
                },
              },
            },
          }
        : undefined,
  });

  if (!neighborhood || !city || neighborhood.citySlug !== citySlug) {
    return <Navigate to={city ? `/cities/${city.slug}` : "/cities"} replace />;
  }

  const relatedServices = neighborhood.services
    .map((s) => serviceBySlug(s))
    .filter(Boolean);
  const otherNeighborhoods = neighborhoodsByCity(citySlug)
    .filter((n) => n.slug !== neighborhoodSlug)
    .slice(0, 6);

  return (
    <div className="bg-[#050505] pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative min-h-[80svh] w-full overflow-hidden border-b border-[#1e1e1e]">
        <img
          src={DEFAULT_HERO}
          alt={`${neighborhood.name}, ${city.name}, Manitoba`}
          className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 opacity-45"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 65% 55% at 25% 45%, transparent 0%, rgba(5,5,5,0.9) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-16 md:pt-24 pb-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Neighborhood
            </p>
            <div className="mt-6 space-y-3">
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  City
                </p>
                <Link
                  to={`/cities/${city.slug}`}
                  data-testid="neighborhood-city-link"
                  className="font-display uppercase tracking-tight text-white text-base link-underline"
                >
                  {city.name}
                </Link>
              </div>
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Region
                </p>
                <p className="font-display uppercase tracking-tight text-white text-base">
                  {city.region}
                </p>
              </div>
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Character
                </p>
                <p className="font-editorial italic text-white text-sm md:text-base">
                  {neighborhood.tag}
                </p>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <p className="font-body uppercase tracking-[0.18em] text-[#a0a0a0] text-xs mb-4">
              {city.name} · Neighborhood
            </p>
            <RevealText
              lines={neighborhood.name.split(" ")}
              className="font-display font-black uppercase text-white tracking-tightest leading-[0.88] text-[clamp(3rem,10vw,10rem)]"
              stagger={0.1}
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
            >
              {neighborhood.hero}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32 space-y-20 md:space-y-24">
        {neighborhood.sections.map((sec, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
            data-testid={`neighborhood-section-${i}`}
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Chapter {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight">
                {sec.h}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {sec.p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Services most relevant to this neighborhood */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 mb-10">
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Services in {neighborhood.name}
              </p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
                Coverage disciplines{" "}
                <span className="font-editorial italic font-normal text-[#a0a0a0]">
                  most relevant to
                </span>{" "}
                {neighborhood.name}
              </h3>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {relatedServices.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}/${city.slug}`}
                  data-testid={`neighborhood-service-${s.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                >
                  <span className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                    <span className="font-editorial italic text-[#FF3B30] not-italic mr-2">
                      {s.number}
                    </span>
                    {s.name} · {city.name}
                  </span>
                  <ArrowUpRight
                    className="text-white/40 group-hover:text-[#FF3B30]"
                    size={16}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Keyword tag band */}
      <section className="py-12 border-b border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {Array.from(new Set(neighborhood.keywords)).map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Other neighborhoods */}
      {otherNeighborhoods.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
            Other {city.name} neighborhoods
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {otherNeighborhoods.map((n) => (
              <Link
                key={n.slug}
                to={`/cities/${city.slug}/${n.slug}`}
                data-testid={`other-neighborhood-${n.slug}`}
                className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-5 transition-colors"
              >
                <p className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                  {n.name}
                </p>
                <p className="mt-1 font-editorial italic text-[#6a6a6a] text-xs">
                  {n.tag}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="border-t border-[#1e1e1e] py-6">
        <ol className="mx-auto max-w-[1600px] px-6 md:px-10 flex flex-wrap gap-2 font-body text-xs uppercase tracking-[0.14em] text-[#a0a0a0]">
          <li>
            <Link to="/" className="hover:text-white">
              Index
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to="/cities" className="hover:text-white">
              Coverage
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to={`/cities/${city.slug}`} className="hover:text-white">
              {city.name}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-white">{neighborhood.name}</li>
        </ol>
      </nav>
    </div>
  );
}
